import { useEffect, useRef, useState, type ReactNode } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AppLayout } from "../../components/layout";
import {
  Alert,
  Button,
  StatusBadge,
  EmptyState,
  CopyButton,
  ConfirmDialog,
  Card,
  DataTable,
  Modal,
  Tabs,
  useNow,
  formatDuration,
  MetricCard,
} from "../../components/core";
import { LiveLineChart } from "../../components/charts";
import { http } from "../../lib/api";
import { subscribeRealtimeEvents } from "../../lib/ws";
import { useUiStore } from "../../store/ui";
import {
  PLAN_STATE_LABEL,
  planBlockMessage,
  type StreamDto,
  type StreamPlanState,
  type DestinationDto,
  type StreamLiveStatsDto,
  type StreamSessionDto,
  type StreamBandwidthDto,
  type StreamViewerStatsDto,
  type ViewerDeviceDto,
  type ViewerCountryDto,
  type StreamRecordingDto,
  type StorageQuotaDto,
} from "@livestreamx/shared-types";
import { StreamKeyField } from "../../components/StreamKeyField";
import { attachLiveHls, type LiveHlsStatus } from "../../lib/live-hls";
import { NAV } from "./nav";

/* ------------------------------------------------------------------ */
/*  Icons — inline stroke SVGs, same family/geometry as the admin nav. */
/* ------------------------------------------------------------------ */
function Svg({ children, size = 16 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const I = {
  back: <Svg size={18}><path d="m15 18-6-6 6-6" /></Svg>,
  live: <Svg size={18}><circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" /><path d="M8.5 15.5a5 5 0 0 1 0-7M15.5 8.5a5 5 0 0 1 0 7M5.6 18.4a9 9 0 0 1 0-12.8M18.4 5.6a9 9 0 0 1 0 12.8" /></Svg>,
  play: <Svg><path d="M6 4.6v14.8L19 12z" /></Svg>,
  stop: <Svg><rect x="6" y="6" width="12" height="12" rx="2.5" /></Svg>,
  record: <Svg><circle cx="12" cy="12" r="7" /><circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" /></Svg>,
  recordStop: <Svg><rect x="8" y="8" width="8" height="8" rx="1.5" /></Svg>,
  refresh: <Svg><path d="M20.5 12a8.5 8.5 0 1 1-2.5-6" /><path d="M21 4v5h-5" /></Svg>,
  trash: <Svg><path d="M3.5 6h17" /><path d="M18.5 6l-1 13.5a2 2 0 0 1-2 1.5H8.5a2 2 0 0 1-2-1.5L5.5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4.2A1.2 1.2 0 0 1 10.2 3h3.6A1.2 1.2 0 0 1 15 4.2V6" /></Svg>,
  external: <Svg><path d="M18 13.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5.5" /><path d="M15 3h6v6" /><path d="M10.5 13.5 21 3" /></Svg>,
  clock: <Svg><circle cx="12" cy="12" r="9" /><path d="M12 7.2V12l3.2 2" /></Svg>,
  eye: <Svg><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" /></Svg>,
  bolt: <Svg><path d="M13.5 2 5 13h6l-1 9 8.5-11h-6z" /></Svg>,
  monitor: <Svg><rect x="2.5" y="3.5" width="19" height="13" rx="2" /><path d="M8.5 21h7M12 16.5V21" /></Svg>,
  upload: <Svg><path d="M12 19.5V5" /><path d="m5.5 11.5 6.5-6.5 6.5 6.5" /></Svg>,
  download: <Svg><path d="M12 4.5V19" /><path d="m5.5 12.5 6.5 6.5 6.5-6.5" /></Svg>,
  swap: <Svg><path d="M7 20V4M7 4 4 7M7 4l3 3" /><path d="M17 4v16M17 20l3-3M17 20l-3-3" /></Svg>,
  broadcast: <Svg><circle cx="12" cy="12" r="2.2" /><path d="M16.2 7.8a6 6 0 0 1 0 8.4M7.8 16.2a6 6 0 0 1 0-8.4M19 5a10 10 0 0 1 0 14M5 19A10 10 0 0 1 5 5" /></Svg>,
  key: <Svg><circle cx="7.5" cy="15.5" r="3.5" /><path d="m10.2 12.8 9.3-9.3" /><path d="m15.5 5.5 3 3" /></Svg>,
  activity: <Svg><path d="M3 12h4l2.5-6 4 12 2.5-6h5" /></Svg>,
  users: <Svg><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></Svg>,
  code: <Svg><path d="m16 18 6-6-6-6M8 6l-6 6 6 6" /></Svg>,
  settings: <Svg><circle cx="12" cy="12" r="3" /><path d="M4.2 12a7.8 7.8 0 0 1 .1-1.2L2.4 9.5l1.8-3.1 2 .9a7.8 7.8 0 0 1 2-1.2l.3-2.2h3.6l.3 2.2a7.8 7.8 0 0 1 2 1.2l2-.9 1.8 3.1-1.9 1.3a7.8 7.8 0 0 1 0 2.4l1.9 1.3-1.8 3.1-2-.9a7.8 7.8 0 0 1-2 1.2l-.3 2.2h-3.6l-.3-2.2a7.8 7.8 0 0 1-2-1.2l-2 .9-1.8-3.1 1.9-1.3A7.8 7.8 0 0 1 4.2 12Z" /></Svg>,
  grid: <Svg><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" /></Svg>,
  plug: <Svg><path d="M9 2.5v5M15 2.5v5" /><path d="M6.5 7.5h11v3.5a5.5 5.5 0 0 1-11 0z" /><path d="M12 16.5V21" /></Svg>,
  trend: <Svg><path d="M3 17.5 9.5 11l4 4L21 7.5" /><path d="M21 7.5h-5M21 7.5v5" /></Svg>,
  plus: <Svg><path d="M12 5v14M5 12h14" /></Svg>,
  arrow: <Svg><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></Svg>,
  alert: <Svg><path d="M12 9.5v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></Svg>,
};


/* ---------------------------- helpers ---------------------------- */
function fmt(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 3600).toString().padStart(2, "0")}:${Math.floor((s % 3600) / 60)
    .toString()
    .padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
}
const kbps = (v: number | null | undefined) => (v == null ? "—" : `${v} kbps`);
const text = (v: number | string | null | undefined) => (v == null ? "—" : String(v));
/** `—` until the first sample lands, so a fresh page never flashes a zero. */
const num = (v: number | null | undefined): string => (v == null ? "—" : String(v));
const fmtWhen = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const fmtBytes = (b: number): string => {
  const u = ["B", "KB", "MB", "GB"];
  let v = b;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${i === 0 ? v : v.toFixed(1)} ${u[i]}`;
};
const clock = (t: number): string =>
  new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

/* ------------------------------------------------------------------ */
/*  HLS player preview (16:9, self-healing)                            */
/* ------------------------------------------------------------------ */
function PlayerPreview({ hlsUrl }: { hlsUrl: string | null }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<LiveHlsStatus>("connecting");
  const [detail, setDetail] = useState<string | null>(null);
  useEffect(() => {
    setStatus("connecting");
    setDetail(null);
    if (!hlsUrl || !videoRef.current) return;
    // Self-healing player: live streams survive short muxer/encoder hiccups,
    // so retry instead of dying with "Stream unavailable".
    const handle = attachLiveHls(videoRef.current, hlsUrl, (next, info) => {
      setStatus(next);
      setDetail(info ?? null);
    });
    return () => handle.destroy();
  }, [hlsUrl]);
  return (
    <div className="sd-player">
      <video ref={videoRef} controls autoPlay playsInline muted className="sd-player-video" />
      {(status === "error" || status === "reconnecting") && (
        <div className="sd-player-error">
          <div className="font-medium">{status === "error" ? "Stream unavailable" : "Reconnecting…"}</div>
          <div className="text-muted text-xs">{detail ?? "Waiting for the stream to come back…"}</div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stream detail page                                                 */
/* ------------------------------------------------------------------ */
export function StreamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ui = useUiStore();
  const [stream, setStream] = useState<StreamDto | null>(null);
  const [dests, setDests] = useState<DestinationDto[]>([]);
  const [showRegen, setShowRegen] = useState(false);
  const [regenConf, setRegenConf] = useState(false);
  const [delConf, setDelConf] = useState(false);
  /** Reason the server refused (or the client predicted) a go-live; popup when set. */
  const [goLiveBlocked, setGoLiveBlocked] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [recBusy, setRecBusy] = useState(false);
  /** Plan storage quota vs disk — drives the remaining-space line under the hero. */
  const [storage, setStorage] = useState<StorageQuotaDto | null>(null);
  const [activeTab, setActiveTab] = useState("destinations");
  const [liveStats, setLiveStats] = useState<StreamLiveStatsDto | null>(null);

  const load = () => {
    if (!id) return;
    void http
      .get<StreamDto>(`/api/tenant/streams/${id}`)
      .then(setStream)
      .catch(() => {});
    void http
      .get<DestinationDto[]>(`/api/tenant/streams/${id}/destinations`)
      .then(setDests)
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const unsubscribe = subscribeRealtimeEvents((_e) => void load());
    return unsubscribe;
  }, [id]);

  // Relay status only advances via the controller's async status reports, and
  // the realtime socket does not cover every transition — poll so the badge and
  // the reconnect counter actually move.
  useEffect(() => {
    if (!id) return;
    let stopped = false;
    const tick = () =>
      void http
        .get<StreamDto>(`/api/tenant/streams/${id}`)
        .then((s) => {
          if (!stopped) {
            setStream(s);
            setRecording(Boolean(s.recordingEnabled));
          }
        })
        .catch(() => {});
    void tick();
    const timer = setInterval(tick, 3_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [id]);

  // Bitrates are read from MediaMTX, which only reports lifetime byte counters,
  // so the API needs a second sample to produce a rate — poll rather than wait
  // for a realtime event that the controller never emits for health samples.
  useEffect(() => {
    if (!id) return;
    let stopped = false;
    const tick = () =>
      void http
        .get<StreamLiveStatsDto>(`/api/tenant/streams/${id}/live-stats`)
        .then((s) => {
          if (!stopped) setLiveStats(s);
        })
        .catch(() => {});
    void tick();
    const timer = setInterval(tick, 5_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [id]);

  // Plan storage quota: polled so the remaining-space line keeps up with files
  // being written and with the watchdog stopping recording on a full quota.
  useEffect(() => {
    if (!id) return;
    let stopped = false;
    const tick = () =>
      void http
        .get<StorageQuotaDto>("/api/tenant/streams/storage")
        .then((s) => {
          if (!stopped) setStorage(s);
        })
        .catch(() => {});
    void tick();
    const timer = setInterval(tick, 15_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [id]);

  // Canonical detail URL shows the stream name slug, not the UUID.
  useEffect(() => {
    if (stream && id !== (stream.slug || stream.id)) {
      navigate(`/streams/${stream.slug || stream.id}`, { replace: true });
    }
  }, [stream, id, navigate]);

  const goLive = () => {
    if (!id) return;
    // Blocked streams never reach the API: the popup names the reason instead of
    // a generic failure, and the server refuses anyway if the plan lapsed since
    // the last poll.
    const reason = planBlockMessage(stream?.planState);
    if (reason) {
      setGoLiveBlocked(reason);
      return;
    }
    void http
      .put(`/api/tenant/streams/${id}/status`, { status: "LIVE" })
      .then(load)
      .catch((e: unknown) => {
        setGoLiveBlocked(e instanceof Error ? e.message : "Streaming is blocked for this stream.");
        void load();
      });
  };

  const stop = async () => {
    if (!id) return;
    try {
      await http.put(`/api/tenant/streams/${id}/status`, { status: "OFFLINE" });
      ui.toast("info", "Stopped");
      load();
    } catch (e) {
      ui.toast("danger", "Error", (e as Error).message);
    }
  };

  const regen = async () => {
    if (!id) return;
    try {
      await http.post(`/api/tenant/streams/${id}/regenerate-key`, {});
      setRegenConf(false);
      setShowRegen(false);
      ui.toast("success", "Key regenerated");
      load();
    } catch (e) {
      ui.toast("danger", "Error", (e as Error).message);
    }
  };

  const del = async () => {
    if (!id) return;
    try {
      await http.delete(`/api/tenant/streams/${id}`);
      ui.toast("success", "Deleted");
      navigate("/streams");
    } catch (e) {
      ui.toast("danger", "Error", (e as Error).message);
    }
  };

  // 0 GB plan / quota exhausted: the API would 403, so the Start button is
  // disabled and the handler never fires a doomed POST (no console error).
  // Stop stays available — ending a recording never needs quota.
  const storageBlocked =
    !!storage && (storage.quotaGb <= 0 || storage.remainingBytes <= 0);

  const toggleRecording = async () => {
    if (!id || recBusy) return;
    if (!recording && storageBlocked) return;
    setRecBusy(true);
    try {
      const next = !recording;
      const res = await http.post<{ ok: boolean; storage?: StorageQuotaDto }>(
        `/api/tenant/streams/${id}/recording`,
        { enabled: next },
      );
      setRecording(next);
      if (res.storage) setStorage(res.storage);
      ui.toast(
        next ? "success" : "info",
        next ? "Recording started" : "Recording stopped",
        res.storage
          ? `${fmtBytes(res.storage.remainingBytes)} of ${res.storage.quotaGb} GB storage remaining · files auto-delete after ${res.storage.retentionDays} days`
          : undefined,
      );
    } catch (e: unknown) {
      // Quota refusals (0 GB plan, quota full) arrive here with the API's message.
      ui.toast("danger", "Error", e instanceof Error ? e.message : "Failed to toggle recording");
      void http
        .get<StorageQuotaDto>("/api/tenant/streams/storage")
        .then(setStorage)
        .catch(() => {});
    } finally {
      setRecBusy(false);
    }
  };

  if (!stream)
    return (
      <AppLayout brand="LiveStreamX" navGroups={NAV}>
        <EmptyState title="Loading…" body="Loading stream details." />
      </AppLayout>
    );

  const live = stream.status === "LIVE";
  // Non-ACTIVE plan: the page still opens (URLs/key stay valid), but the banner
  // and the go-live popup explain why nothing will publish until a renewal.
  const blockedPlan: StreamPlanState | null =
    stream.planState && stream.planState !== "ACTIVE" ? stream.planState : null;
  const liveSec = stream.startedAt ? Math.floor((Date.now() - new Date(stream.startedAt).getTime()) / 1000) : 0;
  const embedSnippet = `<iframe src="${stream.playerUrl}?hls=${stream.hlsUrl || ''}" width="100%" height="500" frameborder="0" allowfullscreen></iframe>`;
  const tabs = [
    { id: "destinations", label: "Destinations", icon: I.broadcast, badge: dests.length },
    { id: "files", label: "Files", icon: I.download },
    { id: "health", label: "Health", icon: I.activity },
    { id: "bandwidth", label: "Bandwidth", icon: I.swap },
    { id: "viewers", label: "Viewers", icon: I.users },
    { id: "sessions", label: "Sessions", icon: I.monitor },
    { id: "embed", label: "Embed", icon: I.code },
    { id: "settings", label: "Settings", icon: I.settings },
  ];

  const kpi: {
    label: string;
    value: string | number;
    hint: string;
    icon: ReactNode;
    tone: "primary" | "success" | "info" | "warning" | "danger";
  }[] = [
    {
      label: "Uptime",
      value: live && liveSec > 0 ? fmt(liveSec) : "—",
      hint: live ? "Since ingest started" : "Stream is offline",
      icon: I.clock,
      tone: "primary",
    },
    { label: "Viewers", value: num(liveStats?.viewers), hint: "Concurrent readers", icon: I.eye, tone: "info" },
    { label: "Video bitrate", value: kbps(liveStats?.videoKbps), hint: "Live ingest rate", icon: I.bolt, tone: "success" },
    {
      label: "Resolution",
      value: text(liveStats?.resolution),
      hint: text(liveStats?.videoCodec ?? "Reported by the encoder"),
      icon: I.monitor,
      tone: "primary",
    },
    { label: "Input", value: kbps(liveStats?.inputKbps), hint: "Publisher → server", icon: I.upload, tone: "info" },
    { label: "Output", value: kbps(liveStats?.outputKbps), hint: "Server → viewers", icon: I.download, tone: "warning" },
    {
      label: "Destinations",
      value: dests.length,
      hint: dests.length === 0 ? "None configured yet" : "RTMP fan-out targets",
      icon: I.broadcast,
      tone: "success",
    },
    {
      label: "Stream key",
      value: stream.streamKeyLast4 ? `••••${stream.streamKeyLast4}` : "—",
      hint: "Last 4 characters",
      icon: I.key,
      tone: "warning",
    },
  ];

  return (
    <>
      <AppLayout brand="LiveStreamX" navGroups={NAV}>
        {/* ── Body: main column + right sidebar (top-aligned) ──── */}
        <div className="sd-layout">
          <div className="sd-main">
            {/* ── Plan gate: lapsed streams stay visible so the tenant can renew ── */}
            {blockedPlan ? (
              <Alert kind="danger">
                <strong>{PLAN_STATE_LABEL[blockedPlan]}</strong> — {planBlockMessage(blockedPlan)} The stream
                keeps its URL and key; renew it from <Link to="/streams">the streams page</Link> and
                streaming starts again.
              </Alert>
            ) : null}
            {/* ── Hero ─────────────────────────────────────────── */}
            <header className="sd-hero">
              <div className="sd-hero-main">
                <Link to="/streams" className="sd-back" aria-label="Back to streams">
                  {I.back}
                </Link>
                <span className="sd-hero-icon" aria-hidden>
                  {I.live}
                </span>
                <div className="sd-hero-text">
                  <div className="sd-hero-line">
                    <h1 className="sd-hero-title">{stream.name}</h1>
                    <StatusBadge status={stream.status} />
                    {live ? (
                      <span className="sd-live">
                        <span className="sd-live-dot" />
                        LIVE
                      </span>
                    ) : null}
                    {live && liveSec > 0 ? (
                      <span className="sd-chip">
                        {I.clock}
                        {fmt(liveSec)}
                      </span>
                    ) : null}
                    {liveStats?.resolution ? (
                      <span className="sd-chip">
                        {I.monitor}
                        {liveStats.resolution}
                      </span>
                    ) : null}
                    {liveStats?.viewers != null ? (
                      <span className="sd-chip">
                        {I.eye}
                        {liveStats.viewers} watching
                      </span>
                    ) : null}
                  </div>
                  <div className="sd-hero-sub">
                    <span className="sd-mono">/streams/{stream.slug || stream.id}</span>
                    <span className="sd-sep" />
                    <span className="sd-mono">key ••••{stream.streamKeyLast4 || "——"}</span>
                    <span className="sd-sep" />
                    <span>
                      {dests.length} destination{dests.length === 1 ? "" : "s"}
                    </span>
                    {stream.description ? (
                      <>
                        <span className="sd-sep" />
                        <span>{stream.description}</span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="sd-hero-actions">
                {live ? (
                  <Button variant="danger" onClick={() => void stop()}>
                    {I.stop}
                    Stop stream
                  </Button>
                ) : (
                  <Button onClick={goLive}>
                    {I.play}
                    Go live
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={toggleRecording}
                  disabled={recBusy || (!recording && storageBlocked)}
                  title={
                    recording
                      ? "Stop recording"
                      : storageBlocked
                        ? "Start recording unavailable — storage quota reached, upgrade your plan"
                        : "Start recording"
                  }
                >
                  {recording ? I.recordStop : I.record}
                  {recording ? "Stop recording" : "Start recording"}
                </Button>
                <a className="btn secondary" href={stream.playerUrl ?? undefined} target="_blank" rel="noreferrer">
                  {I.external}
                  Open player
                </a>
                <Button
                  variant="secondary" onClick={() => setShowRegen(true)}>
                  {I.refresh}
                  Regenerate key
                </Button>
                <Button
                  variant="ghost" onClick={() => setDelConf(true)}>
                  {I.trash}
                  Delete
                </Button>
              </div>
            </header>

            {/* ── Storage quota: remaining space + the 5-day retention rule ── */}
            {storage ? (
              <Alert
                kind={
                  storage.quotaGb <= 0 || storage.remainingBytes <= 0
                    ? "danger"
                    : "info"
                }
              >
                {storage.quotaGb <= 0 ? (
                  <>
                    <strong>0 GB storage quota</strong> — your plan stores no recordings, so
                    Start recording is blocked. Upgrade your plan to record.
                  </>
                ) : storage.remainingBytes <= 0 ? (
                  <>
                    <strong>Storage quota full</strong> — {fmtBytes(storage.usedBytes)} of{" "}
                    {storage.quotaGb} GB used, 0 GB remaining. Delete recordings or upgrade
                    your plan to keep recording.
                  </>
                ) : (
                  <>
                    Storage: {fmtBytes(storage.usedBytes)} of {storage.quotaGb} GB used —{" "}
                    <strong>{fmtBytes(storage.remainingBytes)} remaining</strong>.
                  </>
                )}{" "}
                Recordings are deleted automatically after {storage.retentionDays} days.
              </Alert>
            ) : null}

            {/* ── KPI grid ─────────────────────────────────────── */}
            <div className="stats-grid sd-kpi-grid">
              {kpi.map((k) => (
                <MetricCard key={k.label} label={k.label} value={k.value} hint={k.hint} icon={k.icon} tone={k.tone} />
              ))}
            </div>

            {/* ── Tabs ─────────────────────────────────────────── */}
            <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
            {activeTab === "destinations" && <DestinationsPanel id={id ?? ""} dests={dests} reload={load} />}
            {activeTab === "files" && <Recordings id={id ?? ""} />}
            {activeTab === "health" && <StreamHealth id={id ?? ""} liveStats={liveStats} live={live} />}
            {activeTab === "bandwidth" && <Bandwidth id={id ?? ""} />}
            {activeTab === "viewers" && <ViewerStats id={id ?? ""} live={live} />}
            {activeTab === "sessions" && <Sessions id={id ?? ""} live={live} />}
            {activeTab === "embed" && <EmbedPanel snippet={embedSnippet} playerUrl={stream.playerUrl} />}
            {activeTab === "settings" && (
              <SettingsPanel
                stream={stream}
                onRegen={() => setShowRegen(true)}
                onDelete={() => setDelConf(true)}
              />
            )}
          </div>

          {/* ── Sidebar ─────────────────────────────────────────── */}
          <aside className="sd-aside">
            <Card
              title="Live preview"
              action={live ? <span className="sd-live"><span className="sd-live-dot" />ON AIR</span> : null}
            >
              {live && stream.hlsUrl ? (
                <PlayerPreview hlsUrl={stream.hlsUrl} />
              ) : (
                <EmptyState
                  title="Stream is offline"
                  body="The preview appears as soon as an encoder connects."
                />
              )}
              {live && liveStats ? (
                <div className="sd-preview-meta">
                  <span className="sd-chip">{I.monitor}{text(liveStats.resolution)}</span>
                  <span className="sd-chip">{I.bolt}{kbps(liveStats.videoKbps)}</span>
                  <span className="sd-chip">{I.eye}{num(liveStats.viewers)}</span>
                </div>
              ) : null}
            </Card>

            <IngestCard stream={stream} />
            <PlaybackCard stream={stream} />
          </aside>
        </div>
      </AppLayout>

      {showRegen && (
        <Modal title="Regenerate stream key" onClose={() => setShowRegen(false)}>
          <p className="text-sm">
            This issues a new ingest key for <strong>{stream.name}</strong> and immediately invalidates the
            current one.
          </p>
          <p className="text-sm text-muted">
            Your encoder will be disconnected until you paste the new key into OBS (Settings → Stream → Custom
            Service). Playback URLs and destinations stay untouched.
          </p>
          <div className="flex justify-end gap-2 mt-3">
            <Button
                  variant="secondary" onClick={() => setShowRegen(false)}>
              Cancel
            </Button>
            <Button
                  onClick={() => setRegenConf(true)}>{I.refresh} Regenerate</Button>
          </div>
        </Modal>
      )}
      {goLiveBlocked && (
        <Modal title="Cannot go live" onClose={() => setGoLiveBlocked(null)}>
          <p className="text-sm" style={{ marginTop: 0 }}>{goLiveBlocked}</p>
          <p className="text-sm text-muted">
            The stream URL and key stay exactly as they are — once the plan is active again,
            streaming works with no new setup.
          </p>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="secondary" onClick={() => setGoLiveBlocked(null)}>Close</Button>
            <Link to="/streams">
              <Button>Renew plan</Button>
            </Link>
          </div>
        </Modal>
      )}
      {regenConf && (
        <ConfirmDialog
          title="Regenerate stream key?"
          body="Any encoder still using the old key will stop publishing. This cannot be undone."
          confirmLabel="Regenerate"
          onConfirm={() => void regen()}
          onCancel={() => setRegenConf(false)}
        />
      )}
      {delConf && (
        <ConfirmDialog
          title="Delete this stream?"
          body="The stream, its key and all of its destinations are permanently removed."
          confirmLabel="Delete"
          onConfirm={() => void del()}
          onCancel={() => setDelConf(false)}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Ingest / playback — always visible in the right column            */
/* ------------------------------------------------------------------ */
function IngestCard({ stream }: { stream: StreamDto }) {
  return (
    <Card title="Ingest (RTMP)">
      <div className="sd-field">
        <span className="sd-field-label">Server URL</span>
        <div className="sd-url-row">
          <code className="sd-url">{stream.rtmpUrl}</code>
          <CopyButton text={stream.rtmpUrl} />
        </div>
      </div>
      <div className="sd-field">
        <span className="sd-field-label">Stream key</span>
        <StreamKeyField streamId={stream.id} last4={stream.streamKeyLast4} serverUrl={stream.rtmpUrl} />
      </div>
      <div className="sd-field">
        <span className="sd-field-label">SRT URL</span>
        <div className="sd-url-row">
          <code className="sd-url">{stream.srtUrl}</code>
          <CopyButton text={stream.srtUrl} />
        </div>
      </div>
      <div className="text-muted text-xs">OBS → Settings → Stream → Custom Service.</div>
    </Card>
  );
}

function PlaybackCard({ stream }: { stream: StreamDto }) {
  return (
    <Card
      title="Playback"
      action={
        <a className="btn secondary sm" href={stream.playerUrl ?? "#"} target="_blank" rel="noreferrer">
          {I.external}
          Open
        </a>
      }
    >
      <div className="sd-field">
        <span className="sd-field-label">HLS URL</span>
        <div className="sd-url-row">
          <code className="sd-url">{stream.hlsUrl ?? "—"}</code>
          <CopyButton text={stream.hlsUrl ?? ""} />
        </div>
      </div>
      <div className="sd-field">
        <span className="sd-field-label">Player URL</span>
        <div className="sd-url-row">
          <code className="sd-url">{stream.playerUrl ?? "—"}</code>
          <CopyButton text={stream.playerUrl ?? ""} />
        </div>
      </div>
      <div className="text-muted text-xs">Share the player link, or point any HLS player at the HLS URL.</div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Destinations panel                                                 */
/* ------------------------------------------------------------------ */
function DestinationsPanel({ id, dests, reload }: { id: string; dests: DestinationDto[]; reload: () => void }) {
  const ui = useUiStore();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DestinationDto | null>(null);

  const act = async (d: DestinationDto, action: 'enable' | 'disable' | 'test' | 'delete', label: string) => {
    setBusy(`${d.id}:${action}`);
    if (action === 'delete') {
      setBusy(`${d.id}:delete`);
      try {
        await http.delete(`/api/tenant/streams/${id}/destinations/${d.id}`);
        ui.toast('success', `"${d.name}" deleted`);
        setConfirmDelete(null);
        reload();
      } catch (e) {
        ui.toast('danger', 'Delete failed', (e as Error).message);
      } finally {
        setBusy(null);
      }
      return;
    }
    try {
      const r = await http.post<{ ok?: boolean; latencyMs?: number; error?: string }>(
        `/api/tenant/streams/${id}/destinations/${d.id}/${action}`,
        {},
      );
      if (action === 'test') {
        ui.toast(
          r.ok ? 'success' : 'danger',
          r.ok ? `${d.name} reachable in ${r.latencyMs}ms` : `${d.name} unreachable`,
          r.error,
        );
      } else {
        ui.toast('success', `${label} — ${d.name}`);
      }
      reload();
    } catch (e) {
      ui.toast('danger', `${label} failed`, (e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="sd-panel-head">
        <h2>Destinations</h2>
        <Link to={`/streams/${id}/destinations/create`}>
          <Button size="sm">
            {I.plus}
            Add destination
          </Button>
        </Link>
      </div>
      {dests.length === 0 ? (
        <EmptyState
          title="No destinations"
          body="Add YouTube, Facebook, Twitch, LinkedIn or a custom RTMP target to broadcast everywhere."
          action={
            <Link to={`/streams/${id}/destinations/create`}>
              <Button>Add destination</Button>
            </Link>
          }
        />
      ) : (
        <div className="sd-dest-grid">
          {dests.map((d) => (
            <article key={d.id} className="card sd-dest">
              <div className="sd-dest-head">
                <span className="sd-dest-icon" aria-hidden>
                  {I.broadcast}
                </span>
                <div className="sd-dest-title">
                  <strong>{d.name}</strong>
                  <span className="text-muted text-xs">{d.platform}</span>
                </div>
                <StatusBadge status={d.status} />
              </div>
              <div className="sd-url-row">
                <code className="sd-url">{d.rtmpUrl}</code>
                <CopyButton text={d.rtmpUrl} />
              </div>
              <div className="sd-dest-stats">
                {d.bitrateKbps > 0 ? (
                  <span className="sd-chip">
                    {I.bolt}
                    {d.bitrateKbps} kbps
                  </span>
                ) : null}
                {d.fps > 0 ? (
                  <span className="sd-chip">
                    {I.monitor}
                    {d.fps} fps
                  </span>
                ) : null}
                {d.reconnectCount > 0 ? (
                  <span className="sd-chip">
                    {I.refresh}
                    {d.reconnectCount} reconnects
                  </span>
                ) : null}
              </div>
              {d.lastError ? (
                <div className="alert danger">
                  {I.alert}
                  {d.lastError.slice(0, 140)}
                </div>
              ) : null}
              <div className="sd-dest-actions">
                <Link to={`/streams/${id}/destinations/${d.id}/edit`}>
                  <Button variant="secondary" size="sm">Edit</Button>
                </Link>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy === `${d.id}:test`}
                  onClick={() => void act(d, 'test', 'Test')}
                >
                  {I.bolt}
                  Test
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy === `${d.id}:enable`}
                  onClick={() => void act(d, 'enable', d.enabled ? 'Re-push' : 'Enable')}
                >
                  {I.refresh}
                  {d.enabled ? 'Re-push' : 'Enable'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy === `${d.id}:disable`}
                  onClick={() => void act(d, 'disable', 'Disable')}
                >
                  {d.enabled ? 'Disable' : 'Disabled'}
                </Button>
                <Button
                  onClick={() => setConfirmDelete(d)}
                  variant="danger"
                  size="sm"
                  disabled={busy === `${d.id}:delete`}
                >
                  {I.trash}
                  Delete
                </Button>
              </div>
            </article>
          ))}
          <ConfirmDialog
            open={confirmDelete !== null}
            title="Delete destination"
            body={`Are you sure you want to delete "${confirmDelete?.name}"? This action cannot be undone.`}
            confirmLabel="Delete"
            onConfirm={() => confirmDelete && act(confirmDelete, 'delete', 'Delete')}
            onCancel={() => setConfirmDelete(null)}
          />
        </div>
      )}
    </div>
  );
}

const UPLOAD_COLOR = "#6366f1";
const DOWNLOAD_COLOR = "#06b6d4";

/* ------------------------------------------------------------------ */
/*  Polling hooks — a 5s cadence keeps these tabs live without a socket. */
/* ------------------------------------------------------------------ */
function usePolled<T>(url: string): T | null {
  const [data, setData] = useState<T | null>(null);
  useEffect(() => {
    let stopped = false;
    const tick = () =>
      void http
        .get<T>(url)
        .then((s) => {
          if (!stopped) setData(s);
        })
        .catch(() => {});
    void tick();
    const timer = setInterval(tick, 5_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [url]);
  return data;
}
const useBandwidth = (id: string) => usePolled<StreamBandwidthDto>(`/api/tenant/streams/${id}/bandwidth`);
const useViewerStats = (id: string) => usePolled<StreamViewerStatsDto>(`/api/tenant/streams/${id}/viewer-stats`);
const useSessions = (id: string) => usePolled<StreamSessionDto[]>(`/api/tenant/streams/${id}/sessions`);
const useRecordings = (id: string) =>
  usePolled<StreamRecordingDto[]>(`/api/tenant/streams/${id}/recordings`);

/* ------------------------------------------------------------------ */
/*  Recordings panel (VOD files written by MediaMTX)                   */
/* ------------------------------------------------------------------ */

function Recordings({ id }: { id: string }) {
  const rows = useRecordings(id);
  const quota = usePolled<StorageQuotaDto>("/api/tenant/streams/storage");
  const [playing, setPlaying] = useState<StreamRecordingDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<StreamRecordingDto | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const ui = useUiStore();
  const list = rows ?? [];
  const totalBytes = list.reduce((n, r) => n + r.sizeBytes, 0);

  const deleteRecording = async (recording: StreamRecordingDto) => {
    setBusy(`${recording.name}:delete`);
    try {
      await http.delete(`/api/tenant/streams/${id}/recordings/${encodeURIComponent(recording.name)}`);
      ui.toast('success', `"${recording.name}" deleted`);
      setConfirmDelete(null);
      window.location.reload();
    } catch (e) {
      ui.toast('danger', 'Delete failed', (e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="sd-stack">
      <div className="sd-panel-head">
        <h2>Files</h2>
        <span className="sd-chip">
          {I.download}
          {list.length} recording{list.length === 1 ? "" : "s"}
          {list.length > 0 ? ` • ${fmtBytes(totalBytes)}` : ""}
        </span>
      </div>
      <p className="text-muted text-sm">
        Recordings are kept for {quota?.retentionDays ?? 5} days, then the file and its
        details are deleted automatically.
        {quota
          ? ` Storage: ${fmtBytes(quota.usedBytes)} of ${quota.quotaGb} GB used · ${fmtBytes(quota.remainingBytes)} remaining.`
          : ""}
      </p>
      {list.length === 0 ? (
        <EmptyState
          title="No recordings yet"
          body="Every publish session is recorded automatically and kept for 5 days. Files appear here as soon as your encoder connects."
        />
      ) : (
        <DataTable<StreamRecordingDto>
          rows={list}
          columns={[
            { key: "name", header: "File", render: (r) => <span className="sd-mono">{r.name}</span> },
            { key: "size", header: "Size", render: (r) => fmtBytes(r.sizeBytes) },
            { key: "when", header: "Last written", render: (r) => fmtWhen(r.recordedAt) },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setPlaying(r)}>
                    {I.play}
                    Play
                  </Button>
                  <a className="btn secondary sm" href={r.url} download={r.name}>
                    {I.download}
                    Download
                  </a>
                  <Button
                    onClick={() => setConfirmDelete(r)}
                    variant="danger"
                    size="sm"
                    disabled={busy === `${r.name}:delete`}
                  >
                    {I.trash}
                    Delete
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}

      {playing ? (
        <Modal title={playing.name} onClose={() => setPlaying(null)}>
          <video src={playing.url} controls autoPlay playsInline className="sd-player-video" />
          <div className="flex justify-end mt-3">
            <a className="btn secondary" href={playing.url} download={playing.name}>
              {I.download}
              Download ({fmtBytes(playing.sizeBytes)})
            </a>
          </div>
        </Modal>
      ) : null}

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete recording"
        body={`Are you sure you want to delete "${confirmDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => confirmDelete && deleteRecording(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function Sessions({ id, live }: { id: string; live: boolean }) {
  const sessions = useSessions(id) ?? [];
  // Re-render every second so Duration keeps counting between polls.
  const now = useNow(1000);
  return (
    <div>
      <div className="sd-panel-head">
        <h2>Sessions</h2>
        <span className="sd-chip">
          {I.monitor}
          {sessions.length} active
        </span>
      </div>
      {sessions.length === 0 ? (
        <EmptyState
          title={live ? "No viewers yet" : "Stream is offline"}
          body={
            live
              ? "Sessions appear here the moment someone plays this stream."
              : "Go live and share the player link to start collecting viewer sessions."
          }
        />
      ) : (
        <DataTable<StreamSessionDto>
          rows={sessions}
          columns={[
            { key: "no", header: "No", render: (s) => s.no },
            { key: "protocol", header: "Protocol", render: (s) => s.protocol },
            { key: "ip", header: "IP", render: (s) => text(s.ip) },
            { key: "transmitted", header: "Transmitted", render: (s) => fmtBytes(s.transmittedBytes) },
            {
              key: "duration",
              header: "Duration",
              render: (s) => formatDuration((now - new Date(s.startedAt).getTime()) / 1000),
            },
            {
              key: "ua",
              header: "User Agent",
              render: (s) => (
                <span className="text-xs" style={{ overflowWrap: "anywhere" }}>
                  {text(s.userAgent)}
                </span>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Health panel — both live feeds combined into one read               */
/* ------------------------------------------------------------------ */
function StreamHealth({
  id,
  liveStats,
  live,
}: {
  id: string;
  liveStats: StreamLiveStatsDto | null;
  live: boolean;
}) {
  const bw = useBandwidth(id);
  const now = useNow(1000);
  const online = Boolean(liveStats?.live) && Boolean(bw?.live);
  const upSec = bw?.sessionStartedAt
    ? Math.floor((now - new Date(bw.sessionStartedAt).getTime()) / 1000)
    : 0;
  const kpis = [
    {
      label: "Stream status",
      value: online ? "Healthy" : "Offline",
      hint: online ? `Streaming for ${fmt(upSec)}` : "No active ingest",
      icon: online ? I.activity : I.alert,
      tone: (online ? "success" : "danger") as "success" | "danger",
    },
    {
      label: "Bandwidth",
      value: online ? kbps(bw?.currentKbps) : "—",
      hint: online ? `${kbps(bw?.downloadKbps)} downstream` : "Waiting for data",
      icon: I.swap,
      tone: "primary" as const,
    },
    {
      label: "Viewers",
      value: num(liveStats?.viewers),
      hint: "Concurrent readers",
      icon: I.users,
      tone: "info" as const,
    },
    {
      label: "Transferred",
      value: online ? fmtBytes((bw?.upBytes ?? 0) + (bw?.downBytes ?? 0)) : "—",
      hint: "Ingest + egress this session",
      icon: I.download,
      tone: "warning" as const,
    },
  ];
  const rows = [
    {
      metric: "Video codec",
      value: text(liveStats?.videoCodec),
      description: "Compression format your encoder publishes video in.",
    },
    {
      metric: "Audio codec",
      value: text(liveStats?.audioCodec),
      description: "Compression format your encoder publishes audio in.",
    },
    {
      metric: "Resolution",
      value: text(liveStats?.resolution),
      description: "Frame size reported by the ingest session.",
    },
    {
      metric: "Uptime",
      value: upSec > 0 ? fmt(upSec) : "—",
      description: "Time since the current ingest session started.",
    },
  ];
  return (
    <div className="sd-stack">
      <div className="stats-grid sd-kpi-grid">
        {kpis.map((k) => (
          <MetricCard key={k.label} label={k.label} value={k.value} hint={k.hint} icon={k.icon} tone={k.tone} />
        ))}
      </div>
      <Card
        title="Health metrics"
        action={<StatusBadge status={online ? "LIVE" : "OFFLINE"} label={online ? "Healthy" : "Offline"} />}
      >
        <DataTable<{ metric: string; value: string; description: string }>
          rows={rows}
          columns={[
            { key: "metric", header: "Metric", render: (r) => r.metric },
            { key: "value", header: "Value", render: (r) => <span className="font-medium">{r.value}</span> },
            {
              key: "description",
              header: "Description",
              render: (r) => <span className="text-muted text-sm">{r.description}</span>,
            },
          ]}
        />
      </Card>
      {!online ? (
        <EmptyState
          title={live ? "Waiting for the first samples" : "Stream is offline"}
          body="Health metrics populate automatically as soon as this stream goes live."
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Bandwidth panel                                                    */
/* ------------------------------------------------------------------ */
function Bandwidth({ id }: { id: string }) {
  const bw = useBandwidth(id);
  const points = bw?.points ?? [];
  const labels = points.map((p) => clock(p.t));
  const elapsed = bw?.sessionStartedAt
    ? (Date.now() - new Date(bw.sessionStartedAt).getTime()) / 1000
    : 0;
  const kpis = [
    {
      label: "Current",
      value: bw?.live ? kbps(bw.currentKbps) : "—",
      hint: "Live bandwidth usage",
      icon: I.bolt,
      tone: "primary" as const,
    },
    {
      label: "Average",
      value: bw?.live ? kbps(bw.averageKbps) : "—",
      hint: "Average this session",
      icon: I.trend,
      tone: "info" as const,
    },
    {
      label: "Peak",
      value: bw?.live ? kbps(bw.peakKbps) : "—",
      hint: "Peak this session",
      icon: I.activity,
      tone: "warning" as const,
    },
    {
      label: "Total",
      value: bw?.live ? fmtBytes(bw.totalBytes) : "—",
      hint: "Data transferred",
      icon: I.download,
      tone: "success" as const,
    },
  ];
  return (
    <div className="sd-stack">
      <div className="sd-panel-head">
        <h2>Bandwidth</h2>
        {bw?.live ? (
          <span className="sd-chip">
            {I.clock}
            Session {formatDuration(elapsed)} · sampled every 5s
          </span>
        ) : null}
      </div>
      <div className="stats-grid sd-kpi-grid">
        {kpis.map((k) => (
          <MetricCard key={k.label} label={k.label} value={k.value} hint={k.hint} icon={k.icon} tone={k.tone} />
        ))}
      </div>
      <div className="sd-grid-2">
        <LiveLineChart
          title="Upload Bandwidth"
          labels={labels}
          values={points.map((p) => p.upKbps)}
          color={UPLOAD_COLOR}
          yLabel="kbps"
        />
        <LiveLineChart
          title="Download Bandwidth"
          labels={labels}
          values={points.map((p) => p.downKbps)}
          color={DOWNLOAD_COLOR}
          yLabel="kbps"
        />
      </div>
      <Card title="Bandwidth information">
        <DataTable<StreamBandwidthDto>
          rows={bw ? [bw] : []}
          columns={[
            { key: "up", header: "Upload Rate", render: (b) => kbps(b.uploadKbps) },
            { key: "down", header: "Download Rate", render: (b) => kbps(b.downloadKbps) },
            {
              key: "status",
              header: "Status",
              render: (b) => (
                <StatusBadge status={b.live ? "LIVE" : "OFFLINE"} label={b.live ? "Streaming" : "Offline"} />
              ),
            },
            {
              key: "points",
              header: "Data Points",
              render: (b) =>
                b.dataPoints === 0 ? "—" : `${b.dataPoints} samples · ${clock(b.points[b.points.length - 1].t)}`,
            },
          ]}
        />
      </Card>
      {!bw?.live ? (
        <EmptyState
          title="No bandwidth data"
          body="Upload and download rates appear here as soon as the stream goes live."
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Viewers panel                                                      */
/* ------------------------------------------------------------------ */
function ViewerStats({ id, live }: { id: string; live: boolean }) {
  const stats = useViewerStats(id);
  const watching = (stats?.currentViewers ?? 0) > 0;
  const kpis = [
    {
      label: "Current viewers",
      value: num(stats?.currentViewers),
      hint: "One row per browser",
      icon: I.eye,
      tone: "primary" as const,
    },
    {
      label: "Peak viewers",
      value: num(stats?.peakViewers),
      hint: "Highest audience this session",
      icon: I.trend,
      tone: "info" as const,
    },
    {
      label: "Total views",
      value: num(stats?.totalViews),
      hint: "Distinct viewers since going live",
      icon: I.users,
      tone: "success" as const,
    },
    {
      label: "Avg watch time",
      value: stats?.avgWatchSeconds == null ? "—" : formatDuration(stats.avgWatchSeconds),
      hint: "Average viewer session length",
      icon: I.clock,
      tone: "warning" as const,
    },
  ];
  return (
    <div className="sd-stack">
      <div className="sd-panel-head">
        <h2>Viewer stats</h2>
        <span className="sd-chip">
          {I.refresh}
          {watching ? "Live · refreshed every 5s" : live ? "No viewers right now" : "Stream is offline"}
        </span>
      </div>
      <div className="stats-grid sd-kpi-grid">
        {kpis.map((k) => (
          <MetricCard key={k.label} label={k.label} value={k.value} hint={k.hint} icon={k.icon} tone={k.tone} />
        ))}
      </div>
      <div className="sd-grid-2">
        <Card title="Device breakdown">
          {stats?.devices.length ? (
            <DataTable<ViewerDeviceDto>
              rows={stats.devices}
              columns={[
                { key: "device", header: "Device", render: (r) => r.device },
                { key: "viewers", header: "Viewers", render: (r) => String(r.viewers) },
                { key: "percentage", header: "Percentage", render: (r) => `${r.percentage}%` },
              ]}
            />
          ) : (
            <p className="text-muted text-sm">No devices to report yet.</p>
          )}
        </Card>
        <Card title="Top countries">
          {stats?.countries.length ? (
            <DataTable<ViewerCountryDto>
              rows={stats.countries}
              columns={[
                { key: "country", header: "Country", render: (r) => r.country },
                { key: "viewers", header: "Viewers", render: (r) => String(r.viewers) },
              ]}
            />
          ) : (
            <p className="text-muted text-sm">No countries to report yet.</p>
          )}
        </Card>
      </div>
      {!watching ? (
        <EmptyState title="Nobody is watching" body="Viewer stats appear as soon as someone plays this stream." />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Embed panel                                                        */
/* ------------------------------------------------------------------ */
function EmbedPanel({ snippet, playerUrl }: { snippet: string; playerUrl: string | null }) {
  return (
    <div className="sd-stack">
      <Card
        title="Embed code"
        action={
          playerUrl ? (
            <a className="btn secondary sm" href={playerUrl} target="_blank" rel="noreferrer">
              {I.external}
              Preview
            </a>
          ) : null
        }
      >
        <pre className="sd-embed-code">{snippet}</pre>
        <div className="mt-2">
          <CopyButton text={snippet} />
        </div>
      </Card>
      <Card title="Shareable links">
        <div className="sd-field">
          <span className="sd-field-label">Player URL</span>
          <div className="sd-url-row">
            <code className="sd-url">{playerUrl ?? "—"}</code>
            <CopyButton text={playerUrl ?? ""} />
          </div>
        </div>
        <p className="text-muted text-xs">
          Anyone with this link can watch the stream while it is live — no account required.
        </p>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Settings panel                                                     */
/* ------------------------------------------------------------------ */
function SettingsPanel({
  stream,
  onRegen,
  onDelete,
}: {
  stream: StreamDto;
  onRegen: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="sd-grid-2">
      <Card title="Ingest credentials">
        <div className="sd-setting-row">
          <div className="sd-setting-text">
            <span className="sd-setting-icon" aria-hidden>
              {I.key}
            </span>
            <div>
              <div className="sd-setting-title">Regenerate stream key</div>
              <div className="text-muted text-sm">
                Issues a new key and immediately invalidates the current one. Reconnect your encoder afterwards.
              </div>
            </div>
          </div>
          <Button
                  variant="secondary" onClick={onRegen}>
            {I.refresh}
            Regenerate
          </Button>
        </div>
        {/* The server URL lives in the Ingest (RTMP) card only — repeating it
            here made the page show the same RTMP URL twice. */}
      </Card>

      <Card title="Danger zone">
        <div className="sd-setting-row">
          <div className="sd-setting-text">
            <span className="sd-setting-icon danger" aria-hidden>
              {I.trash}
            </span>
            <div>
              <div className="sd-setting-title sd-danger-title">Delete this stream</div>
              <div className="text-muted text-sm">
                Removes the stream, its key and all of its destinations. This cannot be undone.
              </div>
            </div>
          </div>
          <Button
                  variant="danger" onClick={onDelete}>
            {I.trash}
            Delete
          </Button>
        </div>
      </Card>
    </div>
  );
}



